import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import {
  listAgents,
  getAgentV3,
  setAgentV3,
  deleteAgentV3,
  getCmsData,
  getModelsData,
  getCrmData,
  getInstructionIds,
  getToolsData,
  getTalkliveInstances,
} from '../services/api';
import TalkliveSetupModal from './TalkliveSetupModal';
import SearchableDropdown from './SearchableDropdown';
import VfProjectsDropdown from './VfProjectsDropdown';
import { generateAssistantId } from '../utils/assistantId';
import './AgentEditor.css';

function DealerDocModal({ isOpen, value, onChange, onClose }) {
  const [draft, setDraft] = useState(value || '');

  useEffect(() => {
    if (isOpen) setDraft(value || '');
  }, [isOpen, value]);

  if (!isOpen) return null;

  return (
    <div className="agent-modal-overlay" onClick={onClose}>
      <div className="agent-modal-content agent-modal-content--wide" onClick={(e) => e.stopPropagation()}>
        <h2>Dealer documentation</h2>
        <div className="dealer-doc-modal-grid">
          <div>
            <label>Edit (plain / markdown)</label>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={18}
            />
          </div>
          <div>
            <label>Preview</label>
            <div className="dealer-doc-preview">
              <ReactMarkdown>{draft || '_Empty_'}</ReactMarkdown>
            </div>
          </div>
        </div>
        <div className="agent-modal-actions">
          <button
            type="button"
            onClick={() => {
              onChange(draft);
              onClose();
            }}
          >
            Apply
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

const PRODUCT_KEYS = ['webchat', 'talklive', 'campaign', 'hook', 'facebook', 'marketplace'];
const LIST_PRODUCT_KEYS = ['webchat', 'facebook'];

const PRODUCT_LABELS = {
  webchat: 'Webchat',
  talklive: 'TalkLive',
  campaign: 'Campaign',
  hook: 'Hook',
  facebook: 'Facebook Messenger',
  marketplace: 'Marketplace',
};

/** Keep only the latest `_vN` instruction id per base name (same as Dealer Factory). */
const getLatestVersions = (ids) => {
  const versionMap = new Map();
  (Array.isArray(ids) ? ids : []).forEach((id) => {
    const match = String(id).match(/^(.+)_v(\d+)$/);
    if (match) {
      const baseName = match[1];
      const version = parseInt(match[2], 10);
      const current = versionMap.get(baseName);
      if (!current || version > current.version) {
        versionMap.set(baseName, { id, version });
      }
    } else {
      versionMap.set(id, { id, version: 0 });
    }
  });
  return Array.from(versionMap.values()).map((item) => item.id);
};

const emptyAi = () => ({
  model: '',
  instructions_id: '',
  inventory_id: '',
  dealer_doc: { default_doc: '' },
  tools: [],
  tool_responses: {
    tool_response: '',
    tool_response_afterhour: '',
    invalid_phone_fr: '',
    invalid_phone_en: '',
  },
});

const emptyIntegrations = () => ({
  crm: '',
  emails: [],
  adf_emails: [],
  service_emails: [],
  activix_api_secret_id: '',
  hubspot_secret: '',
  activix_hook_sources: [],
  analytics: { d2c_id: '', ga4_id: '' },
});

const emptyModalContent = () => ({
  title: '',
  message: '',
  btn_primary: '',
  btn_secondary: '',
  btn_secondary_chat_message: '',
  btn_sms: '',
});

const newPackId = (product) => `${product}_${Math.random().toString(16).slice(2, 10)}`;

const emptyWebchatPack = () => ({
  id: newPackId('webchat'),
  label: 'Webchat',
  enabled: true,
  site_url: '',
  site_urls: [],
  model: '',
  tools: [],
  vf_id: '',
  webchat_settings: {
    height: '90%',
    mobile_popup: false,
    popup_once: false,
    custom_css: false,
    chatbot_names: { english: '', french: '' },
    slogans: { english: '', french: '' },
    introductions: { english: '', french: '' },
    popups: { english: '', french: '' },
    chat_persistence: 'localStorage',
    embed_element: '',
    color_theme: '',
    image_src_mobile: '',
  },
  modal_settings: {
    enabled: false,
    trigger_delay_ms: 1500,
    subtitle: '',
    content: { fr: emptyModalContent(), en: emptyModalContent() },
  },
  auto_open_time: '',
  auto_open_pages: [],
  sms_prefill_message: { fr: '', en: '' },
  confirmation_sms: '',
  channels: { sms_instructions_id: '', confirmation_instructions_id: '' },
  twilio_sms: '',
  twilio_confirmation: '',
});

const emptyFacebookPack = () => ({
  id: newPackId('facebook'),
  label: 'Facebook Messenger',
  enabled: true,
  model: '',
  tools: [],
  messenger_ids: [],
  instructions_id: '',
});

const isListProductOn = (packs) =>
  Array.isArray(packs) && packs.some((p) => !!p?.enabled);

const primaryWebchatPack = (packs) => {
  if (!Array.isArray(packs) || !packs.length) return null;
  return packs.find((p) => p.enabled) || packs[0];
};

const emptyProducts = () => ({
  webchat: [],
  talklive: {
    enabled: false,
    model: '',
    tools: [],
    numbers: [],
    instances: [],
    activix_config: { enabled: false, advisor_email: '' },
  },
  campaign: {
    enabled: false,
    twilio_number: '',
    campaign_instructions_id: '',
    tool: 'capture_activix',
    skip_sent_within_hours: 24,
    send_hours: { start: 9, end: 17, timezone: 'America/Toronto' },
    campaigns: {},
  },
  // Legacy soak — not shown in product chips; preserved on load if present.
  phenix: {
    enabled: false,
    model: '',
    tools: [],
    instructions_id: '',
    instructions: '',
    follow_up: null,
    twilio_number: '',
  },
  hook: {
    enabled: false,
    model: '',
    tools: [],
    walkout: { enabled: false, days: 3, send_hour: 10, message: '' },
    twilio_number: '',
  },
  facebook: [],
  marketplace: {
    enabled: false,
    model: '',
    tools: [],
    instructions_id: '',
    start_hour: 10,
    end_hour: 22,
    task_interval_minutes: 5,
  },
});

const emptyAgent = () => ({
  assistant_id: '',
  name: '',
  dealer_url: '',
  cms_type: '',
  status: 'demo',
  group: '',
  timestamps: { created_at: '', last_modified: '' },
  ai: emptyAi(),
  integrations: emptyIntegrations(),
  products: emptyProducts(),
});

const listFieldToText = (arr) => (Array.isArray(arr) ? arr.join(', ') : '');
const textToList = (text) =>
  text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

/** Same CRM values as schema CRMName / Dealer Factory. */
const DEFAULT_CRM_OPTIONS = {
  hubspot: 'hubspot',
  activix: 'activix',
  odoo: 'odoo',
  none: 'none',
};

/** Normalize get_crm_names payload (object, array, or nested) into { key: value }. */
const normalizeCrmOptions = (raw) => {
  if (Array.isArray(raw)) {
    const obj = {};
    raw.forEach((v) => {
      if (v != null && v !== '') obj[String(v)] = String(v);
    });
    return Object.keys(obj).length ? obj : { ...DEFAULT_CRM_OPTIONS };
  }
  if (raw && typeof raw === 'object') {
    if (raw.crm_names) return normalizeCrmOptions(raw.crm_names);
    if (raw.names) return normalizeCrmOptions(raw.names);
    const entries = Object.entries(raw).filter(
      ([, v]) => v != null && typeof v !== 'object'
    );
    if (entries.length) {
      return Object.fromEntries(entries.map(([k, v]) => [k, String(v)]));
    }
  }
  return { ...DEFAULT_CRM_OPTIONS };
};

/** Build SearchableDropdown options from { key: value } maps. */
const objectToOptions = (obj, { emptyLabel } = {}) => {
  const opts = [];
  if (emptyLabel != null) opts.push({ key: emptyLabel, value: '' });
  Object.entries(obj || {})
    .map(([k, v]) => ({ key: String(v ?? k), value: typeof v === 'string' ? v : String(v ?? k) }))
    .filter((o) => o.value !== '')
    .sort((a, b) => a.key.localeCompare(b.key))
    .forEach((o) => opts.push(o));
  return opts;
};

const idsToOptions = (ids, emptyLabel = 'None') => [
  ...(emptyLabel != null ? [{ key: emptyLabel, value: '' }] : []),
  ...(ids || []).map((id) => ({ key: id, value: id })),
];

const STATUS_OPTIONS = [
  { key: 'active', value: 'active' },
  { key: 'inactive', value: 'inactive' },
  { key: 'demo', value: 'demo' },
];

const PHENIX_TOOL_DEFAULTS = ['capture_adf', 'capture_lead', 'capture_activix'];
const SMS_CAPTURE_TOOLS = [
  { value: 'capture_sms_activix', label: 'Capture Sms Activix' },
  { value: 'capture_sms_adf', label: 'Capture Sms Adf' },
  { value: 'capture_sms_adf_dynamic', label: 'Capture Sms Adf Dynamic' },
  { value: 'capture_sms_email', label: 'Capture Sms Email' },
];

const mergeWebchatPack = (incoming, def = emptyWebchatPack()) => {
  const inWs = incoming.webchat_settings || {};
  return {
    ...def,
    ...incoming,
    id: incoming.id || def.id || newPackId('webchat'),
    label: incoming.label || def.label || 'Webchat',
    site_url: incoming.site_url ?? def.site_url ?? '',
    enabled: incoming.enabled !== undefined ? !!incoming.enabled : !!def.enabled,
    webchat_settings: {
      ...def.webchat_settings,
      ...inWs,
      chatbot_names: {
        ...def.webchat_settings.chatbot_names,
        ...(inWs.chatbot_names || {}),
      },
      slogans: {
        ...def.webchat_settings.slogans,
        ...(inWs.slogans || {}),
      },
      introductions: {
        ...def.webchat_settings.introductions,
        ...(inWs.introductions || {}),
      },
      popups: {
        ...def.webchat_settings.popups,
        ...(inWs.popups || {}),
      },
    },
    channels: {
      ...def.channels,
      ...(incoming.channels || {}),
    },
    modal_settings: (() => {
      const inModal = incoming.modal_settings || {};
      return {
        ...def.modal_settings,
        ...inModal,
        content: {
          fr: {
            ...def.modal_settings.content.fr,
            ...(inModal.content?.fr || {}),
          },
          en: {
            ...def.modal_settings.content.en,
            ...(inModal.content?.en || {}),
          },
        },
      };
    })(),
    sms_prefill_message: {
      ...def.sms_prefill_message,
      ...(incoming.sms_prefill_message || {}),
    },
    tools: incoming.tools || [],
  };
};

const mergeFacebookPack = (incoming, def = emptyFacebookPack()) => ({
  ...def,
  ...incoming,
  id: incoming.id || def.id || newPackId('facebook'),
  label: incoming.label || def.label || 'Facebook Messenger',
  enabled: incoming.enabled !== undefined ? !!incoming.enabled : !!def.enabled,
  tools: incoming.tools || [],
  messenger_ids: incoming.messenger_ids || [],
});

const normalizeWebchatList = (incoming) => {
  const raw = incoming.webchat ?? incoming.capture;
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : [raw];
  return list.map((pack) => mergeWebchatPack(pack));
};

const normalizeFacebookList = (incoming) => {
  const raw = incoming.facebook;
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : [raw];
  return list.map((pack) => mergeFacebookPack(pack));
};

const mergeProducts = (incoming = {}) => {
  const base = emptyProducts();
  const defaults = emptyProducts();
  base.webchat = normalizeWebchatList(incoming);
  base.facebook = normalizeFacebookList(incoming);
  PRODUCT_KEYS.forEach((key) => {
    if (LIST_PRODUCT_KEYS.includes(key)) return;
    if (incoming[key]) {
      base[key] = { ...base[key], ...incoming[key] };
      if (key === 'talklive') {
        base.talklive.activix_config = {
          ...defaults.talklive.activix_config,
          ...(incoming.talklive.activix_config || {}),
        };
        base.talklive.tools = incoming.talklive.tools || [];
        base.talklive.numbers = incoming.talklive.numbers || [];
        base.talklive.instances = incoming.talklive.instances || [];
      }
      if (key === 'hook') {
        base.hook.walkout = {
          ...defaults.hook.walkout,
          ...(incoming.hook.walkout || {}),
        };
        base.hook.tools = incoming.hook.tools || [];
      }
      if (key === 'marketplace') {
        base.marketplace.tools = incoming.marketplace.tools || [];
      }
      if (key === 'campaign') {
        base.campaign.send_hours = {
          ...defaults.campaign.send_hours,
          ...(incoming.campaign.send_hours || {}),
        };
        base.campaign.campaigns = incoming.campaign.campaigns || {};
      }
    }
  });
  // Legacy phenix pack (not in PRODUCT_KEYS chips) — keep if present on doc.
  if (incoming.phenix) {
    base.phenix = { ...base.phenix, ...incoming.phenix };
  }
  return base;
};

const AgentEditor = () => {
  const [agents, setAgents] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [agent, setAgent] = useState(emptyAgent());
  const [isCreate, setIsCreate] = useState(true);
  const [manualAssistantId, setManualAssistantId] = useState({ enabled: false, value: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', isError: false });
  const [cmsOptions, setCmsOptions] = useState({});
  const [modelOptions, setModelOptions] = useState({});
  const [crmOptions, setCrmOptions] = useState({});
  const [instructionIds, setInstructionIds] = useState([]);
  const [toolOptions, setToolOptions] = useState([]);
  const [talkliveModalOpen, setTalkliveModalOpen] = useState(false);
  const [dealerDocModalOpen, setDealerDocModalOpen] = useState(false);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [cms, models, crm, instructions, tools, list] = await Promise.all([
          getCmsData(),
          getModelsData(),
          getCrmData(),
          getInstructionIds(),
          getToolsData(),
          listAgents(),
        ]);
        setCmsOptions(cms.data || {});
        setModelOptions(models.data || {});
        setCrmOptions(normalizeCrmOptions(crm.data));
        // API shape: { document_ids: string[] } — not a bare array
        const allInstructionIds = instructions.data?.document_ids || [];
        setInstructionIds(getLatestVersions(allInstructionIds));
        const toolsData = tools.data || {};
        const fromApi = Object.entries(toolsData).map(([key, value]) => ({
          value: typeof value === 'string' ? value : key,
          label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        }));
        const seen = new Set(fromApi.map((o) => o.value));
        setToolOptions([
          ...fromApi,
          ...SMS_CAPTURE_TOOLS.filter((o) => !seen.has(o.value)),
        ]);
        const rows = Array.isArray(list.data?.data) ? list.data.data : [];
        setAgents(rows.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        const queryId = new URLSearchParams(window.location.search).get('assistant_id');
        if (queryId) loadAgent(queryId);
      } catch (err) {
        setMessage({ text: 'Failed to load agent metadata.', isError: true });
      }
    };
    loadMeta();
  }, []);

  const refreshList = async () => {
    const list = await listAgents();
    const rows = Array.isArray(list.data?.data) ? list.data.data : [];
    setAgents(rows.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
  };

  const loadAgent = async (assistantId) => {
    if (!assistantId) return;
    setLoading(true);
    setMessage({ text: '', isError: false });
    try {
      const res = await getAgentV3(assistantId);
      const data = res.data?.data?.[0];
      if (!data) throw new Error('Agent not found');
      const products = mergeProducts(data.products);
      // Compat: seed webchat site_url from root dealer_url when pack has none.
      if (data.dealer_url && products.webchat?.length) {
        products.webchat = products.webchat.map((p, i) =>
          !p.site_url && i === 0 ? { ...p, site_url: data.dealer_url } : p
        );
      }
      // Compat: lift legacy integrations.twilio_numbers if still present on older docs.
      const legacyTwilio = data.integrations?.twilio_numbers || {};
      const webchat0 = products.webchat[0];
      if (webchat0) {
        if (legacyTwilio.sms && !webchat0.twilio_sms) webchat0.twilio_sms = legacyTwilio.sms;
        if (legacyTwilio.confirmation && !webchat0.twilio_confirmation) {
          webchat0.twilio_confirmation = legacyTwilio.confirmation;
        }
      }
      if (legacyTwilio.phenix && !products.phenix.twilio_number) {
        products.phenix.twilio_number = legacyTwilio.phenix;
      }
      if (legacyTwilio.campaign && !products.campaign.twilio_number) {
        products.campaign.twilio_number = legacyTwilio.campaign;
      }
      if (legacyTwilio.hook && !products.hook.twilio_number) {
        products.hook.twilio_number = legacyTwilio.hook;
      }
      // Flat campaign_config lift if nested pack missing (pre-migration docs).
      if (data.campaign_config && !data.products?.campaign) {
        products.campaign = {
          ...products.campaign,
          ...data.campaign_config,
          enabled: !!data.campaign_config.enabled,
          campaigns: data.campaign_config.campaigns || {},
        };
      }
      const { twilio_numbers: _ignoredTwilio, ...integrationsRest } = data.integrations || {};
      // Compat: dual-write docs may still expose crm / emails on the flat top-level.
      const crmValue = integrationsRest.crm || data.crm || '';
      setAgent({
        ...emptyAgent(),
        ...data,
        ai: {
          ...emptyAi(),
          ...(data.ai || {}),
          tool_responses: { ...emptyAi().tool_responses, ...(data.ai?.tool_responses || {}) },
        },
        integrations: {
          ...emptyIntegrations(),
          ...integrationsRest,
          crm: crmValue === 'none' ? '' : crmValue,
          emails: integrationsRest.emails || data.emails || [],
          adf_emails: integrationsRest.adf_emails || data.adf_emails || [],
          service_emails: integrationsRest.service_emails || data.service_emails || [],
          activix_api_secret_id:
            integrationsRest.activix_api_secret_id || data.activix_api_secret_id || '',
          hubspot_secret:
            integrationsRest.hubspot_secret || data.hubspot_secret || '',
          analytics: {
            ...emptyIntegrations().analytics,
            ...(data.integrations?.analytics || {}),
            d2c_id: data.integrations?.analytics?.d2c_id || data.d2c_id || '',
            ga4_id: data.integrations?.analytics?.ga4_id || data.ga4_id || '',
          },
        },
        products,
      });
      setIsCreate(false);
      setSelectedId(assistantId);
    } catch (err) {
      setMessage({ text: err.message || 'Failed to load agent.', isError: true });
    } finally {
      setLoading(false);
    }
  };

  const startCreate = () => {
    setIsCreate(true);
    setSelectedId('');
    setAgent(emptyAgent());
    setManualAssistantId({ enabled: false, value: '' });
    setMessage({ text: '', isError: false });
  };

  const setRoot = (field, value) => setAgent((prev) => ({ ...prev, [field]: value }));
  const setAi = (field, value) =>
    setAgent((prev) => ({ ...prev, ai: { ...prev.ai, [field]: value } }));
  const setIntegration = (field, value) =>
    setAgent((prev) => ({
      ...prev,
      integrations: { ...prev.integrations, [field]: value },
    }));
  const setProduct = (product, field, value) =>
    setAgent((prev) => ({
      ...prev,
      products: {
        ...prev.products,
        [product]: { ...prev.products[product], [field]: value },
      },
    }));

  const setPackField = (product, index, field, value) =>
    setAgent((prev) => {
      const list = [...(prev.products[product] || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, products: { ...prev.products, [product]: list } };
    });

  const toggleListProduct = (product, on) => {
    setAgent((prev) => {
      const list = [...(prev.products[product] || [])];
      if (on) {
        if (!list.length) {
          list.push(product === 'webchat' ? emptyWebchatPack() : emptyFacebookPack());
        } else {
          list.forEach((p, i) => {
            list[i] = { ...p, enabled: true };
          });
        }
      } else {
        list.forEach((p, i) => {
          list[i] = { ...p, enabled: false };
        });
      }
      return { ...prev, products: { ...prev.products, [product]: list } };
    });
  };

  const addPack = (product) => {
    setAgent((prev) => ({
      ...prev,
      products: {
        ...prev.products,
        [product]: [
          ...(prev.products[product] || []),
          product === 'webchat' ? emptyWebchatPack() : emptyFacebookPack(),
        ],
      },
    }));
  };

  const removePack = (product, index) => {
    setAgent((prev) => ({
      ...prev,
      products: {
        ...prev.products,
        [product]: (prev.products[product] || []).filter((_, i) => i !== index),
      },
    }));
  };

  const marketplaceError = useMemo(() => {
    const m = agent.products.marketplace;
    if (!m?.enabled) return '';
    if (m.end_hour <= m.start_hour) return 'Marketplace: end hour must be greater than start hour.';
    if (m.task_interval_minutes < 1 || m.task_interval_minutes > 1440) {
      return 'Marketplace: interval must be between 1 and 1440.';
    }
    return '';
  }, [agent.products.marketplace]);

  const cmsSelectOptions = useMemo(
    () => objectToOptions(cmsOptions, { emptyLabel: 'Select CMS' }),
    [cmsOptions]
  );
  const modelSelectOptions = useMemo(
    () => objectToOptions(modelOptions, { emptyLabel: 'Select model' }),
    [modelOptions]
  );
  const modelInheritOptions = useMemo(
    () => objectToOptions(modelOptions, { emptyLabel: 'Inherit (shared AI)' }),
    [modelOptions]
  );
  const crmSelectOptions = useMemo(() => {
    const opts = objectToOptions(crmOptions, { emptyLabel: 'None' });
    const current = agent.integrations?.crm;
    if (current && !opts.some((o) => o.value === current)) {
      opts.push({ key: current, value: current });
    }
    return opts;
  }, [crmOptions, agent.integrations?.crm]);
  const instructionSelectOptions = useMemo(
    () => idsToOptions(instructionIds, 'Inherit (shared AI)'),
    [instructionIds]
  );
  const instructionRequiredOptions = useMemo(
    () => idsToOptions(instructionIds, 'Select instructions'),
    [instructionIds]
  );
  const phenixToolOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    PHENIX_TOOL_DEFAULTS.forEach((v) => {
      seen.add(v);
      opts.push({ key: v, value: v });
    });
    (toolOptions || []).forEach((o) => {
      if (!seen.has(o.value)) {
        seen.add(o.value);
        opts.push({ key: o.label || o.value, value: o.value });
      }
    });
    return opts;
  }, [toolOptions]);

  const buildPayload = (assistantId) => {
    const now = new Date().toISOString();
    const packModel = (pack) => (pack.model ? pack.model : null);
    const packTools = (pack) =>
      Array.isArray(pack.tools) && pack.tools.length > 0 ? pack.tools : null;
    const products = {};
    PRODUCT_KEYS.forEach((key) => {
      const pack = agent.products[key];
      if (!pack) return;
      if (key === 'webchat') {
        const list = Array.isArray(pack) ? pack : [];
        products.webchat = list.map((p) => {
          const ws = p.webchat_settings || {};
          return {
            id: p.id || newPackId('webchat'),
            label: p.label || 'Webchat',
            enabled: !!p.enabled,
            site_url: p.site_url || null,
            site_urls: (p.site_urls || []).map((u) => u.trim()).filter(Boolean),
            model: packModel(p),
            tools: packTools(p),
            vf_id: p.vf_id || null,
            webchat_settings: {
              ...ws,
              height: ws.height || '90%',
              mobile_popup: !!ws.mobile_popup,
              popup_once: !!ws.popup_once,
              custom_css: !!ws.custom_css,
              embed_element: ws.embed_element || null,
              color_theme: ws.color_theme || null,
              image_src_mobile: ws.image_src_mobile || null,
              chat_persistence: ws.chat_persistence || 'localStorage',
            },
            modal_settings: p.modal_settings || null,
            auto_open_time: p.auto_open_time === '' ? null : Number(p.auto_open_time) || p.auto_open_time,
            auto_open_pages: p.auto_open_pages || [],
            sms_prefill_message: p.sms_prefill_message || null,
            confirmation_sms: p.confirmation_sms || null,
            // Deprecated (MCA-276): legacy pack value re-sent untouched as backend fallback until migration.
            tool_responses: p.tool_responses || null,
            channels: {
              sms_instructions_id: p.channels?.sms_instructions_id || null,
              confirmation_instructions_id: p.channels?.confirmation_instructions_id || null,
            },
            twilio_sms: p.twilio_sms || null,
            twilio_confirmation: p.twilio_confirmation || null,
          };
        });
        return;
      }
      if (key === 'facebook') {
        const list = Array.isArray(pack) ? pack : [];
        products.facebook = list.map((p) => ({
          id: p.id || newPackId('facebook'),
          label: p.label || 'Facebook Messenger',
          enabled: !!p.enabled,
          model: packModel(p),
          tools: packTools(p),
          messenger_ids: p.messenger_ids || [],
          instructions_id: p.instructions_id || null,
        }));
        return;
      }
      if (!pack.enabled) {
        // Persist disabled packs lightly so enablement state is explicit
        if (key === 'marketplace') {
          // Only include marketplace when enabled (required schedule fields)
          return;
        }
        products[key] = { enabled: false };
        return;
      }
      if (key === 'marketplace') {
        if (!pack.enabled) return;
        products.marketplace = {
          enabled: true,
          model: packModel(pack),
          tools: packTools(pack),
          instructions_id: pack.instructions_id || null,
          start_hour: Number(pack.start_hour),
          end_hour: Number(pack.end_hour),
          task_interval_minutes: Number(pack.task_interval_minutes),
        };
        return;
      }
      if (key === 'hook') {
        products.hook = {
          enabled: !!pack.enabled,
          model: packModel(pack),
          tools: packTools(pack),
          twilio_number: pack.twilio_number || null,
          walkout: pack.enabled
            ? {
                enabled: !!pack.walkout?.enabled,
                days: pack.walkout?.days != null ? Number(pack.walkout.days) : null,
                send_hour: pack.walkout?.send_hour != null ? Number(pack.walkout.send_hour) : null,
                message: pack.walkout?.message || null,
              }
            : undefined,
        };
        return;
      }
      if (key === 'campaign') {
        products.campaign = {
          enabled: !!pack.enabled,
          twilio_number: pack.twilio_number || null,
          campaign_instructions_id: pack.campaign_instructions_id || null,
          tool: pack.tool || null,
          skip_sent_within_hours: Number(pack.skip_sent_within_hours) || 24,
          send_hours: {
            start: Number(pack.send_hours?.start ?? 9),
            end: Number(pack.send_hours?.end ?? 17),
            timezone: pack.send_hours?.timezone || 'America/Toronto',
          },
          campaigns: pack.campaigns || {},
        };
        return;
      }
      if (key === 'talklive') {
        products.talklive = {
          enabled: !!pack.enabled,
          model: packModel(pack),
          tools: packTools(pack),
          numbers: pack.numbers || [],
          instances: pack.instances || [],
          activix_config: pack.activix_config || null,
        };
        return;
      }
      products[key] = {
        ...pack,
        enabled: !!pack.enabled,
        model: packModel(pack),
        tools: packTools(pack),
      };
    });

    const primary = primaryWebchatPack(agent.products.webchat);
    const dealerUrl = (primary?.site_url || '').trim();

    const integrations = {
      crm: agent.integrations.crm || null,
      emails: agent.integrations.emails || [],
      adf_emails: agent.integrations.adf_emails || [],
      service_emails: agent.integrations.service_emails || [],
      activix_hook_sources: agent.integrations.activix_hook_sources || [],
      activix_api_secret_id: agent.integrations.activix_api_secret_id || null,
      hubspot_secret: agent.integrations.hubspot_secret || null,
      analytics: {
        d2c_id: agent.integrations.analytics?.d2c_id || null,
        ga4_id: agent.integrations.analytics?.ga4_id || null,
      },
    };

    return {
      assistant_id: assistantId,
      name: agent.name.trim(),
      dealer_url: dealerUrl,
      cms_type: agent.cms_type,
      status: agent.status,
      group: agent.group || null,
      timestamps: {
        created_at: agent.timestamps?.created_at || now,
        last_modified: now,
      },
      ai: {
        ...agent.ai,
        tools: agent.ai.tools || [],
        dealer_doc: agent.ai.dealer_doc || { default_doc: '' },
      },
      integrations,
      products,
    };
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!agent.name.trim() || !agent.cms_type || !agent.ai.model) {
      setMessage({ text: 'Name, cms_type, and model are required.', isError: true });
      return;
    }
    if (isCreate && manualAssistantId.enabled && !manualAssistantId.value.trim()) {
      setMessage({ text: 'Please enter an Assistant ID when manual mode is enabled.', isError: true });
      return;
    }
    if (marketplaceError) {
      setMessage({ text: marketplaceError, isError: true });
      return;
    }
    setSaving(true);
    setMessage({ text: '', isError: false });
    try {
      // New agents get their id here, as in DealerFactory: manual value or generated from the name.
      let assistantId = agent.assistant_id.trim();
      if (isCreate) {
        assistantId = manualAssistantId.enabled
          ? manualAssistantId.value.trim()
          : generateAssistantId(agent.name.trim());
      }
      let payload = buildPayload(assistantId);
      // Refresh TalkLive instances from API if TalkLive enabled
      if (payload.products.talklive?.enabled && payload.assistant_id) {
        try {
          const inst = await getTalkliveInstances(payload.assistant_id).catch(() => null);
          if (inst?.data?.instances) {
            payload.products.talklive.instances = inst.data.instances;
            payload.products.talklive.numbers = inst.data.instances
              .map((i) => i.twilio_number)
              .filter(Boolean);
          }
        } catch (_) {
          /* keep local */
        }
      }
      await setAgentV3(payload);
      setMessage({ text: 'Agent saved.', isError: false });
      setIsCreate(false);
      await refreshList();
      await loadAgent(payload.assistant_id);
    } catch (err) {
      setMessage({
        text: err.response?.data?.detail || err.message || 'Failed to save agent.',
        isError: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!window.confirm(`Delete agent ${selectedId}? This removes the agents doc (legacy mirror kept unless you use backend delete_legacy).`)) {
      return;
    }
    try {
      await deleteAgentV3(selectedId, false);
      setMessage({ text: 'Agent deleted from agents collection.', isError: false });
      startCreate();
      await refreshList();
    } catch (err) {
      setMessage({ text: 'Failed to delete agent.', isError: true });
    }
  };

  const productChips = (row) => {
    // Flat list may not include nested packs; best-effort from common flat signals
    const chips = [];
    if (row.vf_id || row.webchat_settings || row.products?.webchat?.length || row.products?.capture) {
      chips.push('Webchat');
    }
    if (row.talklive_instances?.length || row.talklive_numbers?.length) chips.push('TalkLive');
    if (row.campaign_config?.enabled || row.twilio_numbers?.campaign) chips.push('Campaign');
    if (row.phenix_config?.enabled) chips.push('Phenix');
    if (row.walkout_enabled) chips.push('Hook');
    if (row.messenger_ids?.length || row.products?.facebook?.length) chips.push('Messenger');
    return chips;
  };

  const renderProductAiOverrides = (productKey, index) => {
    const isList = LIST_PRODUCT_KEYS.includes(productKey);
    const pack = isList ? agent.products[productKey]?.[index] : agent.products[productKey];
    const setField = (field, value) =>
      isList ? setPackField(productKey, index, field, value) : setProduct(productKey, field, value);
    return (
      <>
        <label>
          Model override
          <SearchableDropdown
            options={modelInheritOptions}
            value={pack?.model || ''}
            onChange={(v) => setField('model', v)}
            placeholder="Inherit (shared AI)"
          />
        </label>
        <label className="full">
          Tools override
          <Select
            isMulti
            options={toolOptions}
            value={toolOptions.filter((o) => (pack?.tools || []).includes(o.value))}
            onChange={(selected) => setField('tools', (selected || []).map((s) => s.value))}
            placeholder="Inherit shared AI tools (leave empty)"
          />
        </label>
      </>
    );
  };

  const setWebchatSettings = (index, patch) => {
    const pack = agent.products.webchat[index];
    setPackField('webchat', index, 'webchat_settings', {
      ...pack?.webchat_settings,
      ...patch,
    });
  };

  const setWebchatLang = (index, field, lang, value) => {
    const pack = agent.products.webchat[index];
    setWebchatSettings(index, {
      [field]: {
        ...(pack?.webchat_settings?.[field] || {}),
        [lang]: value,
      },
    });
  };

  const setModalSettings = (index, patch) => {
    const pack = agent.products.webchat[index];
    setPackField('webchat', index, 'modal_settings', {
      ...pack?.modal_settings,
      ...patch,
    });
  };

  const setModalContent = (index, lang, field, value) => {
    const pack = agent.products.webchat[index];
    setModalSettings(index, {
      content: {
        ...pack?.modal_settings?.content,
        [lang]: {
          ...pack?.modal_settings?.content?.[lang],
          [field]: value,
        },
      },
    });
  };

  return (
    <div className="agent-editor">
      <div className="agent-editor__header">
        <h2>Agents</h2>
        <p className="agent-editor__sub">
          Nested Agent V3 editor (`/agent/get_v3` + `/agent/set`). One agent per client; enable product packs as needed.
        </p>
      </div>

      <div className="agent-editor__toolbar">
        <div className="agent-editor__agent-select">
          <SearchableDropdown
            options={[
              { key: '— New / none —', value: '', valueDisplay: 'Clear selection' },
              ...agents.map((row) => ({
                key: `${row.name || row.assistant_id}`.trim() || row.assistant_id,
                value: row.assistant_id,
                valueDisplay: [
                  row.assistant_id,
                  productChips(row).length ? productChips(row).join(', ') : null,
                ]
                  .filter(Boolean)
                  .join(' — '),
              })),
            ]}
            value={selectedId}
            onChange={(id) => {
              if (id) loadAgent(id);
              else startCreate();
            }}
            placeholder={loading ? 'Loading agents…' : 'Search agent…'}
          />
        </div>
        <button type="button" onClick={startCreate}>New Agent</button>
        {!isCreate && (
          <button type="button" className="danger" onClick={handleDelete}>Delete</button>
        )}
      </div>

      {message.text && (
        <div className={`agent-editor__msg ${message.isError ? 'error' : 'ok'}`}>{message.text}</div>
      )}

      {loading ? (
        <div>Loading…</div>
      ) : (
        <form className="agent-editor__form" onSubmit={handleSave}>
          {isCreate && (
            <section className="agent-section">
              <label>
                <input
                  type="checkbox"
                  checked={manualAssistantId.enabled}
                  onChange={(e) => setManualAssistantId((prev) => ({ ...prev, enabled: e.target.checked }))}
                />
                Use Manual Assistant ID
              </label>
              {manualAssistantId.enabled && (
                <input
                  aria-label="Manual Assistant ID"
                  value={manualAssistantId.value}
                  onChange={(e) => setManualAssistantId((prev) => ({ ...prev, value: e.target.value }))}
                  placeholder="Enter Assistant ID"
                  required
                />
              )}
            </section>
          )}
          <section className="agent-section">
            <h3>1. Overview</h3>
            <div className="agent-grid">
              <label>
                Name
                <input value={agent.name} onChange={(e) => setRoot('name', e.target.value)} required />
              </label>
              {!isCreate && (
                <label>
                  assistant_id
                  <input value={agent.assistant_id} disabled />
                </label>
              )}
              <label>
                CMS
                <SearchableDropdown
                  options={cmsSelectOptions}
                  value={agent.cms_type}
                  onChange={(v) => setRoot('cms_type', v)}
                  placeholder="Select CMS"
                />
              </label>
              <label>
                Status
                <SearchableDropdown
                  options={STATUS_OPTIONS}
                  value={agent.status}
                  onChange={(v) => setRoot('status', v || 'demo')}
                  placeholder="Select status"
                />
              </label>
              <label>
                Group
                <input value={agent.group || ''} onChange={(e) => setRoot('group', e.target.value)} />
              </label>
            </div>
          </section>

          <section className="agent-section">
            <h3>2. Shared AI</h3>
            <div className="agent-grid">
              <label>
                Model
                <SearchableDropdown
                  options={modelSelectOptions}
                  value={agent.ai.model}
                  onChange={(v) => setAi('model', v)}
                  placeholder="Select model"
                />
              </label>
              <label>
                Instructions
                <SearchableDropdown
                  options={instructionRequiredOptions}
                  value={agent.ai.instructions_id}
                  onChange={(v) => setAi('instructions_id', v)}
                  placeholder="Select instructions"
                />
              </label>
              <label>
                Inventory ID
                <input value={agent.ai.inventory_id} onChange={(e) => setAi('inventory_id', e.target.value)} />
              </label>
              <label className="dealer-doc-field">
                Dealer doc
                <div className="dealer-doc-row">
                  <input
                    value={agent.ai.dealer_doc?.default_doc || ''}
                    onChange={(e) => setAi('dealer_doc', { default_doc: e.target.value })}
                    placeholder="Short preview — use Edit for full doc"
                    readOnly
                    onClick={() => setDealerDocModalOpen(true)}
                  />
                  <button type="button" className="dealer-doc-edit-btn" onClick={() => setDealerDocModalOpen(true)}>
                    Edit
                  </button>
                </div>
              </label>
            </div>
            <label className="full">
              Default tools
              <Select
                isMulti
                options={toolOptions}
                value={toolOptions.filter((o) => (agent.ai.tools || []).includes(o.value))}
                onChange={(selected) => setAi('tools', (selected || []).map((s) => s.value))}
              />
            </label>
            <h4>Custom tool responses</h4>
            <div className="agent-grid">
              {[
                ['tool_response', 'Business hours', ''],
                ['tool_response_afterhour', 'After hours', ''],
                [
                  'invalid_phone_fr',
                  'Invalid phone (FR)',
                  "Le numéro de téléphone n'est pas valide. Veuillez vérifier le format et réessayer.",
                ],
                [
                  'invalid_phone_en',
                  'Invalid phone (EN)',
                  'The phone number is not valid. Please check the format and try again.',
                ],
              ].map(([field, label, placeholder]) => (
                <label className="full" key={field}>
                  {label}
                  <input
                    value={agent.ai.tool_responses?.[field] || ''}
                    onChange={(e) =>
                      setAi('tool_responses', { ...agent.ai.tool_responses, [field]: e.target.value })
                    }
                    placeholder={placeholder}
                  />
                </label>
              ))}
            </div>
          </section>

          <section className="agent-section">
            <h3>3. Integrations</h3>
            <div className="agent-grid">
              <label>
                CRM
                <SearchableDropdown
                  options={crmSelectOptions}
                  value={agent.integrations.crm || ''}
                  onChange={(v) => setIntegration('crm', v)}
                  placeholder="None"
                />
              </label>
              <label>
                Activix secret id
                <input
                  value={agent.integrations.activix_api_secret_id || ''}
                  onChange={(e) => setIntegration('activix_api_secret_id', e.target.value)}
                />
              </label>
              <label>
                HubSpot secret
                <input
                  value={agent.integrations.hubspot_secret || ''}
                  onChange={(e) => setIntegration('hubspot_secret', e.target.value)}
                  placeholder="Secret Manager secret name"
                />
              </label>
              <label>
                Emails (comma-separated)
                <input
                  value={listFieldToText(agent.integrations.emails)}
                  onChange={(e) => setIntegration('emails', textToList(e.target.value))}
                />
              </label>
              <label>
                ADF emails
                <input
                  value={listFieldToText(agent.integrations.adf_emails)}
                  onChange={(e) => setIntegration('adf_emails', textToList(e.target.value))}
                />
              </label>
              <label>
                Service emails
                <input
                  value={listFieldToText(agent.integrations.service_emails)}
                  onChange={(e) => setIntegration('service_emails', textToList(e.target.value))}
                />
              </label>
              <label>
                Activix hook sources
                <input
                  value={listFieldToText(agent.integrations.activix_hook_sources)}
                  onChange={(e) => setIntegration('activix_hook_sources', textToList(e.target.value))}
                />
              </label>
              <label>
                GA4
                <input
                  value={agent.integrations.analytics?.ga4_id || ''}
                  onChange={(e) =>
                    setIntegration('analytics', {
                      ...agent.integrations.analytics,
                      ga4_id: e.target.value,
                    })
                  }
                />
              </label>
              <label>
                D2C
                <input
                  value={agent.integrations.analytics?.d2c_id || ''}
                  onChange={(e) =>
                    setIntegration('analytics', {
                      ...agent.integrations.analytics,
                      d2c_id: e.target.value,
                    })
                  }
                />
              </label>
            </div>
          </section>

          <section className="agent-section">
            <h3>4. Products</h3>
            <div className="product-hub">
              {PRODUCT_KEYS.map((key) => {
                const on = LIST_PRODUCT_KEYS.includes(key)
                  ? isListProductOn(agent.products[key])
                  : !!agent.products[key]?.enabled;
                return (
                  <label key={key} className={`product-chip ${on ? 'on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(e) => {
                        if (LIST_PRODUCT_KEYS.includes(key)) {
                          toggleListProduct(key, e.target.checked);
                        } else {
                          setProduct(key, 'enabled', e.target.checked);
                        }
                      }}
                    />
                    {PRODUCT_LABELS[key]}
                  </label>
                );
              })}
            </div>

            {isListProductOn(agent.products.webchat) && (
              <div className="product-panel">
                <h4>Webchat</h4>
                {(agent.products.webchat || []).map((pack, index) => (
                  <div key={pack.id || index} className="product-pack">
                    <div className="agent-grid">
                      <label>
                        Pack id
                        <input value={pack.id || ''} readOnly className="mono" />
                      </label>
                      <label>
                        Label
                        <input
                          value={pack.label || ''}
                          onChange={(e) => setPackField('webchat', index, 'label', e.target.value)}
                        />
                      </label>
                      <label>
                        Site URL
                        <input
                          value={pack.site_url || ''}
                          onChange={(e) => setPackField('webchat', index, 'site_url', e.target.value)}
                        />
                      </label>
                      <label>
                        Additional site URLs (one per line)
                        <textarea
                          rows={3}
                          value={(pack.site_urls || []).join('\n')}
                          onChange={(e) => setPackField('webchat', index, 'site_urls', e.target.value.split('\n'))}
                        />
                      </label>
                      <label className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={!!pack.enabled}
                          onChange={(e) => setPackField('webchat', index, 'enabled', e.target.checked)}
                        />
                        Enabled
                      </label>
                      {renderProductAiOverrides('webchat', index)}
                      <label>
                        VF ID
                        <VfProjectsDropdown
                          value={pack.vf_id || ''}
                          onChange={(value) => setPackField('webchat', index, 'vf_id', value)}
                          placeholder="Select VF project..."
                        />
                      </label>
                      <label>
                        Auto-open time (ms)
                        <input
                          type="number"
                          value={pack.auto_open_time || ''}
                          onChange={(e) => setPackField('webchat', index, 'auto_open_time', e.target.value)}
                        />
                      </label>
                      <label>
                        Auto-open pages (comma-separated)
                        <input
                          value={listFieldToText(pack.auto_open_pages)}
                          onChange={(e) => setPackField('webchat', index, 'auto_open_pages', textToList(e.target.value))}
                        />
                      </label>
                      <label>
                        Confirmation SMS
                        <input
                          value={pack.confirmation_sms || ''}
                          onChange={(e) => setPackField('webchat', index, 'confirmation_sms', e.target.value)}
                        />
                      </label>
                      <label>
                        SMS instructions
                        <SearchableDropdown
                          options={instructionSelectOptions}
                          value={pack.channels?.sms_instructions_id || ''}
                          onChange={(v) =>
                            setPackField('webchat', index, 'channels', {
                              ...pack.channels,
                              sms_instructions_id: v,
                            })
                          }
                          placeholder="Inherit (shared AI)"
                        />
                      </label>
                      <label>
                        Confirmation instructions
                        <SearchableDropdown
                          options={instructionSelectOptions}
                          value={pack.channels?.confirmation_instructions_id || ''}
                          onChange={(v) =>
                            setPackField('webchat', index, 'channels', {
                              ...pack.channels,
                              confirmation_instructions_id: v,
                            })
                          }
                          placeholder="Inherit (shared AI)"
                        />
                      </label>
                      <label>
                        Twilio SMS
                        <input
                          value={pack.twilio_sms || ''}
                          onChange={(e) => setPackField('webchat', index, 'twilio_sms', e.target.value)}
                        />
                      </label>
                      <label>
                        Twilio confirmation
                        <input
                          value={pack.twilio_confirmation || ''}
                          onChange={(e) => setPackField('webchat', index, 'twilio_confirmation', e.target.value)}
                        />
                      </label>
                      <label>
                        SMS prefill (EN)
                        <input
                          value={pack.sms_prefill_message?.en || ''}
                          onChange={(e) =>
                            setPackField('webchat', index, 'sms_prefill_message', {
                              ...pack.sms_prefill_message,
                              en: e.target.value,
                            })
                          }
                        />
                      </label>
                      <label>
                        SMS prefill (FR)
                        <input
                          value={pack.sms_prefill_message?.fr || ''}
                          onChange={(e) =>
                            setPackField('webchat', index, 'sms_prefill_message', {
                              ...pack.sms_prefill_message,
                              fr: e.target.value,
                            })
                          }
                        />
                      </label>
                    </div>

                    <h5>Webchat settings</h5>
                    <div className="agent-grid">
                      <label>
                        Chatbot name (EN)
                        <input
                          value={pack.webchat_settings?.chatbot_names?.english || ''}
                          onChange={(e) => setWebchatLang(index, 'chatbot_names', 'english', e.target.value)}
                        />
                      </label>
                      <label>
                        Chatbot name (FR)
                        <input
                          value={pack.webchat_settings?.chatbot_names?.french || ''}
                          onChange={(e) => setWebchatLang(index, 'chatbot_names', 'french', e.target.value)}
                        />
                      </label>
                      <label>
                        Slogan (EN)
                        <input
                          value={pack.webchat_settings?.slogans?.english || ''}
                          onChange={(e) => setWebchatLang(index, 'slogans', 'english', e.target.value)}
                        />
                      </label>
                      <label>
                        Slogan (FR)
                        <input
                          value={pack.webchat_settings?.slogans?.french || ''}
                          onChange={(e) => setWebchatLang(index, 'slogans', 'french', e.target.value)}
                        />
                      </label>
                      <label>
                        Introduction (EN)
                        <input
                          value={pack.webchat_settings?.introductions?.english || ''}
                          onChange={(e) => setWebchatLang(index, 'introductions', 'english', e.target.value)}
                        />
                      </label>
                      <label>
                        Introduction (FR)
                        <input
                          value={pack.webchat_settings?.introductions?.french || ''}
                          onChange={(e) => setWebchatLang(index, 'introductions', 'french', e.target.value)}
                        />
                      </label>
                      <label>
                        Popup (EN)
                        <input
                          value={pack.webchat_settings?.popups?.english || ''}
                          onChange={(e) => setWebchatLang(index, 'popups', 'english', e.target.value)}
                        />
                      </label>
                      <label>
                        Popup (FR)
                        <input
                          value={pack.webchat_settings?.popups?.french || ''}
                          onChange={(e) => setWebchatLang(index, 'popups', 'french', e.target.value)}
                        />
                      </label>
                      <label>
                        Height
                        <input
                          value={pack.webchat_settings?.height || ''}
                          onChange={(e) => setWebchatSettings(index, { height: e.target.value })}
                        />
                      </label>
                      <label>
                        Color theme
                        <input
                          value={pack.webchat_settings?.color_theme || ''}
                          onChange={(e) => setWebchatSettings(index, { color_theme: e.target.value })}
                        />
                      </label>
                      <label>
                        Embed element
                        <input
                          value={pack.webchat_settings?.embed_element || ''}
                          onChange={(e) => setWebchatSettings(index, { embed_element: e.target.value })}
                        />
                      </label>
                      <label>
                        Chat persistence
                        <SearchableDropdown
                          options={[
                            { key: 'localStorage', value: 'localStorage' },
                            { key: 'sessionStorage', value: 'sessionStorage' },
                            { key: 'memory', value: 'memory' },
                          ]}
                          value={pack.webchat_settings?.chat_persistence || 'localStorage'}
                          onChange={(v) => setWebchatSettings(index, { chat_persistence: v || 'localStorage' })}
                          placeholder="localStorage"
                        />
                      </label>
                      <label>
                        Mobile image src
                        <input
                          value={pack.webchat_settings?.image_src_mobile || ''}
                          onChange={(e) => setWebchatSettings(index, { image_src_mobile: e.target.value })}
                        />
                      </label>
                      <label className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={!!pack.webchat_settings?.mobile_popup}
                          onChange={(e) => setWebchatSettings(index, { mobile_popup: e.target.checked })}
                        />
                        Mobile popup
                      </label>
                      <label className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={!!pack.webchat_settings?.popup_once}
                          onChange={(e) => setWebchatSettings(index, { popup_once: e.target.checked })}
                        />
                        Popup once
                      </label>
                      <label className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={!!pack.webchat_settings?.custom_css}
                          onChange={(e) => setWebchatSettings(index, { custom_css: e.target.checked })}
                        />
                        Custom CSS
                      </label>
                    </div>
                    <p className="hint">
                      Nested webchat assets (icons, image_src, image_settings) are preserved on save if already set.
                    </p>

                    <h5>Modal settings</h5>
                    <label className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={!!pack.modal_settings?.enabled}
                        onChange={(e) => setModalSettings(index, { enabled: e.target.checked })}
                      />
                      Modal enabled
                    </label>
                    <div className="agent-grid">
                      <label>
                        Trigger delay (ms)
                        <input
                          type="number"
                          min={500}
                          max={10000}
                          value={pack.modal_settings?.trigger_delay_ms ?? 1500}
                          onChange={(e) => setModalSettings(index, { trigger_delay_ms: Number(e.target.value) })}
                        />
                      </label>
                      <label>
                        Subtitle
                        <input
                          value={pack.modal_settings?.subtitle || ''}
                          onChange={(e) => setModalSettings(index, { subtitle: e.target.value })}
                        />
                      </label>
                    </div>
                    {['en', 'fr'].map((lang) => (
                      <div key={lang}>
                        <h5>Modal content ({lang.toUpperCase()})</h5>
                        <div className="agent-grid">
                          {['title', 'message', 'btn_primary', 'btn_secondary', 'btn_secondary_chat_message', 'btn_sms'].map(
                            (field) => (
                              <label key={field} className={field === 'message' ? 'full' : undefined}>
                                {field}
                                <input
                                  value={pack.modal_settings?.content?.[lang]?.[field] || ''}
                                  onChange={(e) => setModalContent(index, lang, field, e.target.value)}
                                />
                              </label>
                            )
                          )}
                        </div>
                      </div>
                    ))}

                    <button type="button" className="secondary" onClick={() => removePack('webchat', index)}>
                      Remove pack
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => addPack('webchat')}>
                  Add webchat
                </button>
              </div>
            )}

            {agent.products.talklive?.enabled && (
              <div className="product-panel">
                <h4>TalkLive</h4>
                <div className="agent-grid">
                  {renderProductAiOverrides('talklive')}
                  <label>
                    Numbers (comma-separated)
                    <input
                      value={listFieldToText(agent.products.talklive.numbers)}
                      onChange={(e) => setProduct('talklive', 'numbers', textToList(e.target.value))}
                    />
                  </label>
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={!!agent.products.talklive.activix_config?.enabled}
                      onChange={(e) =>
                        setProduct('talklive', 'activix_config', {
                          ...agent.products.talklive.activix_config,
                          enabled: e.target.checked,
                        })
                      }
                    />
                    Activix phone leads
                  </label>
                  <label>
                    Activix advisor email
                    <input
                      value={agent.products.talklive.activix_config?.advisor_email || ''}
                      onChange={(e) =>
                        setProduct('talklive', 'activix_config', {
                          ...agent.products.talklive.activix_config,
                          advisor_email: e.target.value,
                        })
                      }
                    />
                  </label>
                </div>
                <button
                  type="button"
                  className="talklive-manage-btn"
                  onClick={() => {
                    const id = (agent.assistant_id || '').trim();
                    if (!id) {
                      setMessage({
                        text: 'Save the agent first, then open TalkLive instances to create the first one.',
                        isError: true,
                      });
                      return;
                    }
                    setTalkliveModalOpen(true);
                  }}
                >
                  Manage TalkLive instances
                </button>
                <p className="hint">
                  Instances are managed in the modal (and refreshed on save).
                </p>
              </div>
            )}

            {agent.products.campaign?.enabled && (
              <div className="product-panel">
                <h4>Campaign</h4>
                <p className="hint">
                  Dealer defaults for outbound SMS campaigns (`/v1/campaigns`). Individual
                  campaigns (create / test / start) stay on the Campaign API / client UI.
                  Campaign has no model/tools override in the schema.
                </p>
                <div className="agent-grid">
                  <label>
                    Twilio Campaign
                    <input
                      value={agent.products.campaign.twilio_number || ''}
                      onChange={(e) => setProduct('campaign', 'twilio_number', e.target.value)}
                      placeholder="+1514..."
                    />
                  </label>
                  <label>
                    Campaign instructions
                    <SearchableDropdown
                      options={instructionSelectOptions}
                      value={agent.products.campaign.campaign_instructions_id || ''}
                      onChange={(v) => setProduct('campaign', 'campaign_instructions_id', v)}
                      placeholder="Inherit (shared AI)"
                    />
                  </label>
                  <label>
                    Default tool
                    <SearchableDropdown
                      options={phenixToolOptions}
                      value={agent.products.campaign.tool || 'capture_activix'}
                      onChange={(v) => setProduct('campaign', 'tool', v)}
                      placeholder="Select tool"
                    />
                  </label>
                  <label>
                    Skip sent within (hours)
                    <input
                      type="number"
                      min={0}
                      value={agent.products.campaign.skip_sent_within_hours ?? 24}
                      onChange={(e) =>
                        setProduct('campaign', 'skip_sent_within_hours', Number(e.target.value))
                      }
                    />
                  </label>
                  <label>
                    Send hours start
                    <input
                      type="number"
                      min={0}
                      max={23}
                      value={agent.products.campaign.send_hours?.start ?? 9}
                      onChange={(e) =>
                        setProduct('campaign', 'send_hours', {
                          ...agent.products.campaign.send_hours,
                          start: Number(e.target.value),
                        })
                      }
                    />
                  </label>
                  <label>
                    Send hours end (exclusive)
                    <input
                      type="number"
                      min={1}
                      max={24}
                      value={agent.products.campaign.send_hours?.end ?? 17}
                      onChange={(e) =>
                        setProduct('campaign', 'send_hours', {
                          ...agent.products.campaign.send_hours,
                          end: Number(e.target.value),
                        })
                      }
                    />
                  </label>
                  <label>
                    Timezone
                    <input
                      value={agent.products.campaign.send_hours?.timezone || 'America/Toronto'}
                      onChange={(e) =>
                        setProduct('campaign', 'send_hours', {
                          ...agent.products.campaign.send_hours,
                          timezone: e.target.value,
                        })
                      }
                    />
                  </label>
                </div>
                <p className="hint">
                  Active campaigns on this agent:{' '}
                  {Object.keys(agent.products.campaign.campaigns || {}).length}
                  {' '}(managed via Campaign API). Legacy Phenix log page remains available.
                </p>
                <Link to="/phenix">Open Phenix (Legacy) →</Link>
              </div>
            )}

            {agent.products.hook?.enabled && (
              <div className="product-panel">
                <h4>Hook</h4>
                <div className="agent-grid">
                  {renderProductAiOverrides('hook')}
                </div>
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={!!agent.products.hook.walkout?.enabled}
                    onChange={(e) =>
                      setProduct('hook', 'walkout', {
                        ...agent.products.hook.walkout,
                        enabled: e.target.checked,
                      })
                    }
                  />
                  Walkout enabled
                </label>
                <div className="agent-grid">
                  <label>
                    Twilio Hook
                    <input
                      value={agent.products.hook.twilio_number || ''}
                      onChange={(e) => setProduct('hook', 'twilio_number', e.target.value)}
                    />
                  </label>
                  <label>
                    Days
                    <input
                      type="number"
                      value={agent.products.hook.walkout?.days ?? ''}
                      onChange={(e) =>
                        setProduct('hook', 'walkout', {
                          ...agent.products.hook.walkout,
                          days: e.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    Send hour
                    <input
                      type="number"
                      min={0}
                      max={23}
                      value={agent.products.hook.walkout?.send_hour ?? ''}
                      onChange={(e) =>
                        setProduct('hook', 'walkout', {
                          ...agent.products.hook.walkout,
                          send_hour: e.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    Message
                    <input
                      value={agent.products.hook.walkout?.message || ''}
                      onChange={(e) =>
                        setProduct('hook', 'walkout', {
                          ...agent.products.hook.walkout,
                          message: e.target.value,
                        })
                      }
                    />
                  </label>
                </div>
              </div>
            )}

            {isListProductOn(agent.products.facebook) && (
              <div className="product-panel">
                <h4>Facebook Messenger</h4>
                {(agent.products.facebook || []).map((pack, index) => (
                  <div key={pack.id || index} className="product-pack">
                    <div className="agent-grid">
                      <label>
                        Pack id
                        <input value={pack.id || ''} readOnly className="mono" />
                      </label>
                      <label>
                        Label
                        <input
                          value={pack.label || ''}
                          onChange={(e) => setPackField('facebook', index, 'label', e.target.value)}
                        />
                      </label>
                      <label className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={!!pack.enabled}
                          onChange={(e) => setPackField('facebook', index, 'enabled', e.target.checked)}
                        />
                        Enabled
                      </label>
                      {renderProductAiOverrides('facebook', index)}
                      <label>
                        Messenger IDs (comma-separated)
                        <input
                          value={listFieldToText(pack.messenger_ids)}
                          onChange={(e) =>
                            setPackField('facebook', index, 'messenger_ids', textToList(e.target.value))
                          }
                        />
                      </label>
                      <label>
                        Instructions
                        <SearchableDropdown
                          options={instructionSelectOptions}
                          value={pack.instructions_id || ''}
                          onChange={(v) => setPackField('facebook', index, 'instructions_id', v)}
                          placeholder="Inherit (shared AI)"
                        />
                      </label>
                    </div>
                    <button type="button" className="secondary" onClick={() => removePack('facebook', index)}>
                      Remove pack
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => addPack('facebook')}>
                  Add Facebook
                </button>
              </div>
            )}

            {agent.products.marketplace?.enabled && (
              <div className="product-panel">
                <h4>Marketplace</h4>
                <p className="hint">Schedule fields are required when Marketplace is enabled.</p>
                <div className="agent-grid">
                  {renderProductAiOverrides('marketplace')}
                  <label>
                    Start hour
                    <input
                      type="number"
                      min={0}
                      max={23}
                      value={agent.products.marketplace.start_hour}
                      onChange={(e) => setProduct('marketplace', 'start_hour', Number(e.target.value))}
                      required
                    />
                  </label>
                  <label>
                    End hour
                    <input
                      type="number"
                      min={0}
                      max={23}
                      value={agent.products.marketplace.end_hour}
                      onChange={(e) => setProduct('marketplace', 'end_hour', Number(e.target.value))}
                      required
                    />
                  </label>
                  <label>
                    Interval (minutes)
                    <input
                      type="number"
                      min={1}
                      max={1440}
                      value={agent.products.marketplace.task_interval_minutes}
                      onChange={(e) =>
                        setProduct('marketplace', 'task_interval_minutes', Number(e.target.value))
                      }
                      required
                    />
                  </label>
                  <label>
                    Instructions
                    <SearchableDropdown
                      options={instructionSelectOptions}
                      value={agent.products.marketplace.instructions_id || ''}
                      onChange={(v) => setProduct('marketplace', 'instructions_id', v)}
                      placeholder="Inherit (shared AI)"
                    />
                  </label>
                </div>
                {marketplaceError && <p className="error-text">{marketplaceError}</p>}
              </div>
            )}
          </section>

          <div className="agent-editor__actions">
            <button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Agent'}</button>
          </div>
        </form>
      )}

      <TalkliveSetupModal
        isOpen={talkliveModalOpen && !!(agent.assistant_id || '').trim()}
        onClose={() => setTalkliveModalOpen(false)}
        assistantId={(agent.assistant_id || '').trim()}
        assistantName={agent.name}
        instructionOptions={instructionIds}
        onSave={(updatedInstances) =>
          setProduct('talklive', 'instances', updatedInstances || [])
        }
      />

      <DealerDocModal
        isOpen={dealerDocModalOpen}
        value={agent.ai.dealer_doc?.default_doc || ''}
        onChange={(text) => setAi('dealer_doc', { default_doc: text })}
        onClose={() => setDealerDocModalOpen(false)}
      />
    </div>
  );
};

export default AgentEditor;
